// Recap landing — platform picker. One live card (Sleeper) plus a disabled
// Yahoo card; clicking Sleeper transitions to RecapImport for that platform.
// Below the cards, a "Load your JSON" panel re-hydrates a downloaded
// almanac.v1 export straight into the report (utils/almanacJsonImport.js) —
// the recovery path for Yahoo leagues now that the API 403s, and an offline
// path for everyone else. It emits the SAME pipelineState shape as
// RecapImport's onPipelineReady, via the onAlmanacLoaded prop.
// Mirrors the visual language of src/components/LandingPage.jsx so the recap
// feels like part of Mock1, not a separate product.
//
// YAHOO IS DISABLED HERE, NOT REMOVED. Yahoo revoked API access for third-party
// apps registered before summer 2026 (confirmed Jul 29, ecosystem-wide, not
// specific to this app): every Fantasy API call returns 403 "This application is
// not authorized to perform this action." The entire integration still exists
// and works the moment access returns — api/yahoo/* is untouched and RecapImport
// still has its yahoo flow — so this is one card going quiet, not a teardown.
//
// The card stays VISIBLE on purpose. Hiding it reads as "this tool doesn't do
// Yahoo"; saying what happened reads as someone who knows what's going on. It
// uses the same non-interactive treatment as the ESPN signpost below: muted
// text, no hover, no cursor, no CTA — nothing that suggests a destination.
//
// NO ESPN CARD — a SIGNPOST LINE instead, below the cards. One card shipped
// here briefly (e58e5f1) and was removed when the ESPN import moved to
// SettingsModal: ESPN provides no matchup data, so it cannot produce an almanac
// at all, and a card in the picker row promises a destination that does not
// exist. The line points at Settings, where ESPN genuinely works, and keeps
// firing capturePlatformSelected("espn") so the demand signal still collects.
//
// ⚠️ A CARD HERE IS A PROMISE ABOUT WHERE IT GOES. RecapImport dispatches on an
// allowlist and AuctionDraftBuilder validates the stage against that same list,
// so a card added here without a flow there lands on a visible "unknown
// platform" panel. Both used to be binaries that fell through to Yahoo, which
// would have made a third card silently open a Yahoo login. Add the card and
// its route together.

import { useState, useEffect, useRef } from "react";
import theme from "../../data/theme";
import { YAHOO_UNAVAILABLE_LEAD, YAHOO_UNAVAILABLE_BODY } from "../../data/platformStatus";
import { loadAlmanacExport, AlmanacImportError } from "../../utils/almanacJsonImport";
import { captureLandingView, capturePlatformSelected, captureAlmanacJsonLoaded, captureAlmanacJsonLoadFailed } from "../../utils/recapAnalytics";
import { captureDataContributionClicked } from "../../utils/onboardingAnalytics";

// One definition of the inline-error box, so the JSON loader's error and a
// failed share link read identically. Was inline in the loader panel; lifted
// when the share route needed the same treatment above the fold.
const errorBoxStyle = {
  background: "rgba(239,68,68,0.10)",
  border: "1px solid rgba(239,68,68,0.35)",
  color: theme.colors.status.danger,
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12,
  fontFamily: theme.fonts.body,
};

// `shareError` is set when /almanac?share=<id> failed to load. It renders at
// the TOP of the landing rather than in the JSON-loader panel: someone who
// arrived from a broken link never scrolled anywhere and would not see a
// message parked below the fold.
export default function RecapLanding({ onPickPlatform, onOpenSettings, onAlmanacLoaded, shareError }) {
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 820);
  // "Load your JSON" — file-picker state. loadError renders inline in the
  // loader panel; the input is hidden and proxied by the visible button.
  const fileInputRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  useEffect(() => {
    captureLandingView();
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handlePick = (platform) => {
    capturePlatformSelected(platform);
    onPickPlatform(platform);
  };

  // Re-hydrate a downloaded almanac.v1 JSON straight into the report.
  // Parse + validate happen fully in-browser (loadAlmanacExport is pure);
  // on success the pipelineState goes up the same path RecapImport uses,
  // so everything downstream of the landing page is unchanged. On failure
  // the error renders inline and the input is reset so re-picking the
  // SAME file fires onChange again (browsers suppress it otherwise).
  const handleAlmanacFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoadError(null);
    setLoadingFile(true);
    try {
      const text = await file.text();
      const state = loadAlmanacExport(text);
      captureAlmanacJsonLoaded({
        platform: state.platform,
        seasonCount: state.seasons?.length || 0,
        ownerCount: state.leagueMetadata?.ownerCount || 0,
        fileSizeKb: Math.round(text.length / 1024),
      });
      if (onAlmanacLoaded) {
        onAlmanacLoaded(state);
        // No setLoadingFile(false) on success — the parent unmounts this
        // page for the report; resetting state on an unmounted component
        // is React's warning, not ours to trigger.
      } else {
        // Prop not wired (shouldn't happen outside tests) — fail visibly
        // instead of spinning forever.
        setLoadError("Loaded the file, but this page isn't wired to show it. Refresh and try again.");
        setLoadingFile(false);
      }
    } catch (err) {
      const msg = err instanceof AlmanacImportError
        ? err.userMessage
        : "Couldn't read that file. If it came from this site's Download JSON button, email mock1app@gmail.com.";
      captureAlmanacJsonLoadFailed({ reason: msg, fileSizeKb: Math.round((file.size || 0) / 1024) });
      setLoadError(msg);
      setLoadingFile(false);
    }
  };

  // Same event as the cards, deliberately — an ESPN-specific event name would
  // not be comparable against sleeper/yahoo in the same funnel, and comparing
  // them is the entire reason to measure this. It routes to Settings instead of
  // onPickPlatform because there is no ESPN flow on this path to route to.
  const handleEspnSignpost = () => {
    capturePlatformSelected("espn");
    onOpenSettings?.();
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const cardStyle = (id) => ({
    flex: 1,
    background: hovering === id ? theme.helpers.brandBg(0.10) : theme.colors.bg.raised,
    border: `1px solid ${hovering === id ? "rgba(255,107,0,0.55)" : theme.colors.border.default}`,
    borderRadius: 14,
    // A1 Patch B — generous padding to match the upscaled type ramp.
    padding: "36px 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 18,
    cursor: "pointer",
    transform: hovering === id ? "scale(1.02)" : "scale(1)",
    transition: "all 0.2s ease",
    minWidth: 0,
  });

  const ctaStyle = {
    background: "transparent",
    color: theme.colors.brand.accent,
    border: `1px solid rgba(255,107,0,0.5)`,
    padding: "12px 28px",
    fontSize: 15,
    fontWeight: theme.fontWeight.heavy,
    fontFamily: theme.fonts.display,
    letterSpacing: "2px",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
  };

  // Shared per-card text styles. Introduced with the (since-removed) third
  // card and kept: one definition is still better than a copy per card.
  const cardTitleStyle = {
    fontSize: 32,
    fontWeight: theme.fontWeight.black,
    color: theme.colors.text.primary,
    letterSpacing: "-0.5px",
  };

  const cardBodyStyle = {
    fontSize: 18,
    color: theme.colors.text.secondary,
    textAlign: "center",
    fontFamily: theme.fonts.body,
    lineHeight: 1.5,
    minHeight: 70,
  };

  // Disabled variant of cardStyle: no hover state to read, no pointer, dimmed
  // border. Same box so the row still balances, nothing that implies a click.
  const disabledCardStyle = {
    ...cardStyle("yahoo-disabled"),
    background: theme.colors.bg.raised,
    border: `1px dashed ${theme.colors.border.default}`,
    cursor: "default",
    transform: "scale(1)",
    opacity: 0.72,
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      fontFamily: theme.fonts.display,
      overflow: "hidden",
      position: "relative",
    }}>
      <div style={{
        position: "absolute", top: "-20%", left: "50%", transform: "translateX(-50%)",
        width: 600, height: 400,
        background: `radial-gradient(ellipse, ${theme.helpers.brandBg()} 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
        textAlign: "center",
        maxWidth: 760,
        width: "100%",
        position: "relative",
        zIndex: 1,
      }}>
        {shareError && (
          <div role="alert" style={{
            ...errorBoxStyle,
            marginBottom: 20,
            textAlign: "left",
            fontSize: 13,
          }}>{shareError}</div>
        )}
        <h1 style={{
          // A1 Patch B — magazine-scale hero. 96px desktop / 64px mobile.
          fontSize: isMobile ? 64 : 96,
          margin: 0,
          fontWeight: theme.fontWeight.black,
          letterSpacing: "-2px",
          color: theme.colors.text.primary,
          textShadow: theme.helpers.brandGlow(0.2),
          lineHeight: 1.02,
        }}>
          League Almanac
        </h1>
        <p style={{
          fontSize: isMobile ? 18 : 22,
          color: theme.colors.text.muted,
          marginTop: 16,
          marginBottom: 44,
          fontFamily: theme.fonts.body,
          lineHeight: 1.45,
        }}>
          Your league's complete history. Preserved forever. Free.
          <br />
          {/* Was "Sleeper is fully automatic. Yahoo takes a quick login." — the
              second half promised a login that now 403s at every call. */}
          Sleeper is fully automatic.
        </p>

        <div style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 18,
          width: "100%",
        }}>
          <div
            style={cardStyle("sleeper")}
            onClick={() => handlePick("sleeper")}
            onMouseEnter={() => setHovering("sleeper")}
            onMouseLeave={() => setHovering(null)}
          >
            <div style={cardTitleStyle}>Sleeper</div>
            <div style={cardBodyStyle}>
              Enter your username → pick the league.
              <br />
              We walk back every prior season automatically.
            </div>
            <button style={ctaStyle}>Pick Sleeper →</button>
          </div>

          {/* No onClick, no hover handlers, no CTA button — see the header note.
              aria-disabled rather than a <button disabled>, because this was
              never a button; it is a card that has stopped being a destination. */}
          <div style={disabledCardStyle} aria-disabled="true">
            <div style={{ ...cardTitleStyle, color: theme.colors.text.muted }}>Yahoo</div>
            <div style={{ ...cardBodyStyle, color: theme.colors.text.muted, fontSize: 16 }}>
              <strong style={{ color: theme.colors.text.secondary }}>{YAHOO_UNAVAILABLE_LEAD}</strong>{" "}
              {YAHOO_UNAVAILABLE_BODY}
            </div>
          </div>
        </div>

        {/* "Load your JSON" — a PANEL, not a third card, because it isn't a
            third platform: it's the other half of the Download JSON button in
            the report sidebar, and post-Yahoo-403 it is the ONLY door back
            into a Yahoo report. Sitting directly under the disabled Yahoo
            card is deliberate — the person most likely to need this is the
            one who just read that card. Unlike the ESPN signpost this gets a
            real CTA, because unlike ESPN there IS a destination: the full
            report, rebuilt in-browser from the archive, no login, no API. */}
        <div style={{
          marginTop: 18,
          background: theme.colors.bg.raised,
          border: `1px solid ${theme.colors.border.default}`,
          borderRadius: 14,
          padding: "18px 22px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: 14,
          textAlign: "left",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 15,
              fontWeight: theme.fontWeight.heavy,
              color: theme.colors.text.primary,
              marginBottom: 4,
            }}>
              Already have your almanac JSON?
            </div>
            <div style={{
              fontSize: 13,
              color: theme.colors.text.muted,
              fontFamily: theme.fonts.body,
              lineHeight: 1.5,
            }}>
              Load the file you downloaded from your report and the full almanac
              rebuilds right here — no login, works even for Yahoo leagues.
            </div>
            {loadError && (
              <div style={{ ...errorBoxStyle, marginTop: 8 }}>{loadError}</div>
            )}
          </div>
          <button
            type="button"
            style={{ ...ctaStyle, width: isMobile ? "100%" : "auto", whiteSpace: "nowrap", opacity: loadingFile ? 0.6 : 1 }}
            disabled={loadingFile}
            onClick={() => fileInputRef.current?.click()}
          >
            {loadingFile ? "Loading…" : "Load JSON →"}
          </button>
          {/* accept covers file managers that honor extensions and those that
              honor MIME; validation is loadAlmanacExport's job either way. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            onChange={handleAlmanacFile}
          />
        </div>

        {/* ESPN signpost — A LINE, NOT A CARD, and the distinction is the whole
            point. A third card sits in the picker row and reads as a third
            destination; there isn't one, because ESPN provides no matchup data
            and so cannot produce an almanac. This says where ESPN DOES work
            without pretending it works here.

            capturePlatformSelected("espn") still fires from the link, which is
            the reason to keep anything here at all: it is the only place the
            ESPN-on-the-Almanac demand signal can be collected, and it stays
            comparable to the two cards' events because it is the same event. */}
        <p style={{
          fontSize: 13,
          color: theme.colors.text.muted,
          marginTop: 20,
          fontFamily: theme.fonts.body,
          lineHeight: 1.55,
        }}>
          <strong style={{ color: theme.colors.text.secondary }}>ESPN:</strong>{" "}
          league settings and draft prices import from{" "}
          <span
            role="link"
            tabIndex={0}
            onClick={handleEspnSignpost}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleEspnSignpost(); } }}
            style={{
              color: theme.colors.brand.accent,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >Settings</span>{" "}
          today. Full Almanac support needs matchup data we don't fetch yet.
        </p>

        <p style={{
          fontSize: 11,
          color: theme.colors.text.faint,
          marginTop: 24,
          fontFamily: theme.fonts.body,
        }}>
          Read-only access — we never modify your league.
        </p>
        <p style={{
          fontSize: 11,
          color: theme.colors.text.faint,
          marginTop: 8,
          fontFamily: theme.fonts.body,
        }}>
          Beta — importing your league? Tell me what breaks:{" "}
          <a href="mailto:mock1app@gmail.com" style={{ color: theme.colors.brand.accent, textDecoration: "none" }}>mock1app@gmail.com</a>
        </p>
        {/* The AI is calibrated off a handful of real auctions — more rooms is
            the single biggest lever on how it prices. Ask plainly, once. */}
        <p style={{
          fontSize: 11,
          color: theme.colors.text.faint,
          marginTop: 8,
          fontFamily: theme.fonts.body,
        }}>
          Have results from your real auction drafts?{" "}
          <a
            href="mailto:mock1app@gmail.com?subject=My%20real%20auction%20results"
            onClick={() => captureDataContributionClicked("almanac")}
            style={{ color: theme.colors.brand.accent, textDecoration: "none" }}
          >Email them over</a>{" "}
          — real rooms make the AI sharper. (League details stay private.)
        </p>
      </div>
    </div>
  );
}
